import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import xhook from 'xhook';
import Echo from 'laravel-echo';

// Polyfills
import 'core-js/es';
import 'core-js/es/promise';
import 'core-js/es/array/includes';
import 'core-js/es/string/pad-start';
import 'core-js/es/string/pad-end';
import 'core-js/es/symbol/async-iterator';
import 'core-js/es/object/get-own-property-descriptors';
import 'core-js/es/object/values';
import 'core-js/es/object/entries';
import 'core-js/es/promise/finally';
import 'core-js/web';
import 'custom-event-polyfill';
import 'url-polyfill';

import { refreshTokenInterceptor } from './actions/Auth';

import {
  getVisitorId,
  setLastActivity,
} from './helpers/Auth';

const baseConsoleError = console.error.bind(console);
const SUPPRESSED_REACT_WARNINGS = [
  'Support for defaultProps will be removed',
  'uses the legacy childContextTypes',
];

console.error = (...args) => {
  if (
    typeof args[0] === 'string'
    && SUPPRESSED_REACT_WARNINGS.some(message => args[0].includes(message))
  ) {
    return;
  }

  baseConsoleError(...args);
};

window._ = require('lodash');

window.getCollectionsRequestCancel = null;
window.getFilesRequestCancel = null;
window.getFiltersRequestCancel = null;

try {
  window.$ = window.jQuery = require('jquery');
} catch (e) {}

window.axios = require('axios').default;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

window.axios.interceptors.request.use((config) => {
  if (Cookies.get('sp_access_token')) {
    config.headers.Authorization = `Bearer ${Cookies.get('sp_access_token')}`;
  }
  if (Cookies.get('sp_switched_user')) {
    config.headers['switched-user-uuid'] = Cookies.get('sp_switched_user');
  }
  if (getVisitorId()) {
    config.headers['sp-vid'] = getVisitorId();
  }

  const token = (new URLSearchParams(location.search)).get('token');
  if (token) {
    config.headers['auth-token'] = token;
  }

  setLastActivity();

  return config;
});

// Refresh access token on failed requests
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRrefreshed = (token) => {
  refreshSubscribers = refreshSubscribers.filter(cb => cb(token));
};

window.axios.interceptors.response.use(response => {
  const headers = response?.headers;

  if (headers && headers['sp-version']) {
    if (!window.sp.version) {
      window.sp.version = headers['sp-version'];
    }

    if (window.sp.version != headers['sp-version']) {
      window.sp.version = headers['sp-version'];
    }
  }

  return response;
});

window.axios.interceptors.response.use(response => {
  return response;
}, error => {
  if (error?.code === 'ERR_CANCELED') {
    return Promise.reject(error);
  }

  const { config, response: { status, headers } } = error;
  const originalRequest = config;

  if (status === 401) {
    if (headers['skip-token-refresh']) {
      return Promise.reject(error);
    }

    if (!isRefreshing && !window.sp.isTokenRefreshing) {
      isRefreshing = true;
      refreshTokenInterceptor().then((newToken) => {
        isRefreshing = false;
        onRrefreshed(newToken);
      });
    }

    const retryOrigReq = new Promise((resolve) => {
      subscribeTokenRefresh((token) => {
        originalRequest.headers.Authorization = 'Bearer ' + token;
        resolve(axios(originalRequest));
      });
    });
    return retryOrigReq;
  } else if (status === 403) {
    const { response: { data }} = error;
    if (data.error === 'password_expired' && data.password_reset_token && window.location.href.indexOf('password-reset') < 0) {
      window.location = `/password-reset?token=${data.password_reset_token}&expired=true`;
    }
    return Promise.reject(error);
  } else {
    return Promise.reject(error);
  }
});

// Forward all XHR requests to Axios
const XHRrequestInterceptorPassToAxios = () => {
  xhook.before((request, callback) => {
    if (request.headers?.SPAuth) {
      request.headers.SPAuth = null;
      request.data = request.body;
      window.axios({
        data: request.body,
        transformResponse: (req) => {
          return req;
        },
        ...request,
      }).then((response) => {
        callback({ text: response.data, ...response });
      }).catch((error) => {
        callback(error);
      });
    } else {
      callback();
    }
  });
};
XHRrequestInterceptorPassToAxios();

/**
 * Next we will register the CSRF Token as a common header with Axios so that
 * all outgoing HTTP requests automatically have it attached. This is just
 * a simple convenience so we don't have to attach every token manually.
 */

const token = document.head.querySelector('meta[name="csrf-token"]');
if (token) {
  window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
} else {
  console.error('CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token');
}

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */

window.Pusher = require('pusher-js');

window.Echo = new Echo({
  broadcaster: 'pusher',
  namespace: '',
  key: process.env.MIX_PUSHER_APP_KEY,
  cluster: 'us',
  wsHost: process.env.MIX_PUSHER_HOST,
  wsPort: process.env.MIX_PUSHER_PORT,
  wssPort: process.env.MIX_PUSHER_PORT,
  forceTLS: false,
  encrypted: true,
  disableStats: true,
  enabledTransports: ['ws', 'wss'],
  auth: {
    headers: {
      Authorization: `Bearer ${Cookies.get('sp_access_token')}`,
      SPAuth: true,
    },
  },
  authorizer: (channel, options) => {
    return {
      authorize: (socketId, callback) => {
        axios.post('/broadcasting/auth', {
          socket_id: socketId,
          channel_name: channel.name,
        }, {
          headers: {
            'user-timezone': moment.tz.guess(true),
          },
        })
        .then(response => {
          callback(null, response.data);
        })
        .catch(error => {
          callback(error);
        });
      },
    };
  },
});
